import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useState } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { Loader2 } from "lucide-react";
import { groupByMonth } from "@/lib/format";
import { buildPages } from "./layout";
import { BookPage } from "./BookPage";
import { PhotoCell } from "./PhotoCell";

const HEADER = 56;
// must mirror the .book-grid breakpoints in book.css
const gridFor = (vw) => ({ cols: vw >= 1440 ? 6 : vw >= 1024 ? 5 : vw >= 768 ? 4 : 2, gap: vw >= 1440 ? 26 : vw >= 768 ? 22 : 12 });

const useGridSize = (el) => {
  const [size, setSize] = useState({ width: 0, vw: typeof window !== "undefined" ? window.innerWidth : 1280 });
  useLayoutEffect(() => {
    if (!el) return undefined;
    const update = () => setSize({ width: el.clientWidth, vw: window.innerWidth });
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, [el]);
  return size;
};

const buildRows = (groups, lead, cols) => {
  const rows = [];
  groups.forEach((g, gi) => {
    if (g.label) rows.push({ type: "h", key: `h-${g.key}-${gi}`, label: g.label, gk: g.key });
    const cells = gi === 0 && lead ? [{ lead: true, key: "lead" }, ...g.pages] : g.pages;
    for (let i = 0; i < cells.length; i += cols) rows.push({ type: "p", key: `r-${cells[i].key}`, cells: cells.slice(i, i + cols) });
  });
  return rows;
};

// Virtualized photo book: only visible rows of pages (+ overscan) are mounted. mode="thumbs": compact square grid.
export const BookGrid = forwardRef(({ items, timeline, lead, hasMore, loading, onLoadMore, empty, mode = "book", ...cellProps }, ref) => {
  const [listEl, setListEl] = useState(null);
  const { width, vw } = useGridSize(listEl);
  const thumbs = mode === "thumbs";
  const book = gridFor(vw);
  const gap = thumbs ? (vw >= 768 ? 8 : 4) : book.gap;
  const cols = thumbs ? Math.max(3, Math.floor((width + gap) / ((vw >= 768 ? 140 : 100) + gap)) || 3) : book.cols;
  const cell = width ? (width - gap * (cols - 1)) / cols : 140;
  const rowH = thumbs ? cell + gap : width ? cell * (4 / 3) + gap : 320;
  const groups = useMemo(
    () => (timeline ? groupByMonth(items) : [{ key: "all", label: null, items }])
      .map((g) => ({ ...g, pages: thumbs ? g.items.map((it) => ({ key: it.id, thumb: it, items: [it] })) : buildPages(g.items) })),
    [items, timeline, thumbs],
  );
  const rows = useMemo(() => buildRows(groups, thumbs ? null : lead, cols), [groups, lead, cols, thumbs]);
  const rowOfItem = useMemo(() => {
    const m = new Map();
    rows.forEach((r, i) => r.type === "p" && r.cells.forEach((c) => c.items?.forEach((it) => m.set(it.id, i))));
    return m;
  }, [rows]);
  const [margin, setMargin] = useState(0);
  useLayoutEffect(() => {
    if (listEl) setMargin(listEl.getBoundingClientRect().top + window.scrollY);
  }, [listEl, width]);

  const v = useWindowVirtualizer({
    count: rows.length,
    estimateSize: (i) => (rows[i]?.type === "h" ? HEADER : rowH),
    overscan: 3,
    scrollMargin: margin,
    getItemKey: (i) => rows[i].key,
  });
  useEffect(() => { v.measure(); }, [rowH, cols, v]);

  useImperativeHandle(ref, () => ({
    scrollToItem: (id) => {
      const idx = rowOfItem.get(id);
      if (idx != null) v.scrollToIndex(idx, { align: "auto" });
    },
  }), [rowOfItem, v]);

  const virtual = v.getVirtualItems();
  const last = virtual[virtual.length - 1]?.index ?? 0;
  useEffect(() => {
    if (hasMore && !loading && rows.length && last >= rows.length - 4) onLoadMore?.();
  }, [last, rows.length, hasMore, loading, onLoadMore]);

  if (!loading && !items.length && (!lead || thumbs)) return <div className="book-empty" data-testid="book-empty">{empty}</div>;

  return (
    <div className={`book ${thumbs ? "is-thumbs" : ""}`} data-testid="book-grid" data-mode={mode}>
      <div ref={setListEl} className="book-virtual" style={{ height: v.getTotalSize() }} data-testid="book-virtual-list" data-rows={rows.length}>
        {virtual.map((vr) => {
          const r = rows[vr.index];
          return (
            <div key={vr.key} className="book-row" style={{ height: vr.size, transform: `translateY(${vr.start - margin}px)` }}>
              {r.type === "h" ? (
                <h2 className="book-month" data-testid={`timeline-month-header-${r.gk}`}>{r.label}</h2>
              ) : (
                <div className={thumbs ? "thumb-grid" : "book-grid"} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap }}>
                  {r.cells.map((c) => (c.lead ? <div key="lead" className="contents">{lead}</div>
                    : c.thumb ? <div key={c.key} className="thumb-cell" style={{ height: cell }}><PhotoCell item={c.thumb} {...cellProps} selected={cellProps.selected?.has(c.thumb.id)} /></div>
                      : <BookPage key={c.key} page={c} {...cellProps} />))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {loading && (
        <div className="flex justify-center py-10 text-white/80" data-testid="book-loading"><Loader2 className="animate-spin" /></div>
      )}
    </div>
  );
});
BookGrid.displayName = "BookGrid";
